import type {ReactElement, RefObject} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {SocialMediaWizardNav} from '@Components/social-media-wizard/components/social-media-wizard-nav';
import {SocialMediaWizardContent} from '@Components/social-media-wizard/components/social-media-wizard-content';
import {SocialAccountsList} from '@Components/social-media-wizard/components/social-accounts-list';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {DesignMetaUpdateInfo, DesignsGraphicMetaPayload} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  getWizardContentSize,
  PUBLISH_PARAM_HANDLER_CLASS,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialAccountConnection} from '@Components/social-media-wizard/components/social-account-connection';
import {SocialAccountButtons} from '@Components/social-media-wizard/components/social-account-buttons';
import {
  useAccountIdsToPublishTo,
  useAccountTypeToConnect,
  useNextButtonText,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useTriggerNext,
  useTriggerPrevious,
  useUpdatePublishingParams,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {getLoadingTextByPublishingAction, getNextWizardStep, getStartingURIForGallery} from '@Libraries/social-media-wizard-library';
import {DesignSelector} from '@Components/design-selector';
import {
  disableNextButton,
  enableNextButton,
  hideAccountLevelErrorMessage,
  initPublishingParams,
  resetAccountResizeStatusForDesignsMap,
  resetSocialPostCoverForAllPublishingParams,
  showNextButton,
  syncEditedDesignId,
  syncPublishingParams,
  updateAccountIdsToPublishTo,
  updateAccountLevelErrorMessage,
  updateAccountNeedsResizeMapForDesigns,
  updateAccountTypeToConnect,
  updateDesignIdsToPublish,
  updateDesignIdsToPublishAndTriggerNext,
  updateDesignSelectorTab,
  updateDesignsGraphicsMeta,
  updateEmphasizedPlatform,
  updateHasSeenConfetti,
  updateHasSeenOnboarding,
  updateHasUserPublishedBefore,
  updateIsGoogleBusinessProfileEnabled,
  updateIsSocialMediaWizardAiCaptionsEnabled,
  updateIsTemplateSelected,
  updateNextButtonIcon,
  updateNextButtonText,
  updateNumParentAccountsConnected,
  updatePost,
  updatePublishingAction,
  updatePublishingParams,
  updateTriggerNext,
  updateTriggerPrevious,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {SocialMediaPostDetails} from '@Components/social-media-wizard/components/social-media-post-details';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {
  getPostId,
  useDesignIdsToPublish,
  useDesignSelectorTab,
  useIsTemplateSelected,
  useSocialMediaWizardContainerQuery,
  useWizardCaptionErrors,
  useWizardConnectedAccounts,
  useWizardDesignsGraphicsMeta,
  useWizardMobileNavView,
  useWizardPublishingParams,
  useWizardTitleErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {SocialMediaMobileFooter} from '@Components/social-media-wizard/components/social-media-mobile-footer';
import {EmbeddedEditorAction, openDesignInEmbeddedEditor} from '@Libraries/embedded-editor';
import type {PosterHashedId} from '@Components/social-media/publish-params.types';
import {MultiPostDesignActionPurposes, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {AccountNeedsResizeMapForDesigns, DesignIsVideoInformation, MultipleDesignMetaForPublishing, SocialPlatform} from '@Components/social-media/social-media.types';
import {PosterResizeRequiredStatus} from '@Components/social-media/social-media.types';
import {getPosterPreviewURL, pollForPosterPreviewChanges} from '@Libraries/poster-library';
import {savePreferredProfileSelectionUser} from '@Components/social-media/social-media-thunk';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import {addClickOutsideListener} from '@Utils/dom.util';
import useHasSeenAiCaptionIntroPopup from '@Hooks/auto-captions/useHasSeenAiCaptionIntroPopup';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {SocialMediaWizardOnboarding} from '@Components/social-media-wizard/components/social-media-wizard-onboarding';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {DesignGallery} from '@Components/design-gallery';
import {TemplateEditorPanel} from '@Components/social-media-wizard/components/template-editor-panel';
import {SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {SocialPostContent} from '@Components/social-media/components/social-post-content-multiposting';
import type {SocialPostsDesignActionModalProps} from '@Components/modals/social-post-design-action-modal';
import {openDesignActionModal} from '@Components/modals/social-post-design-action-modal';
import {getMobilePreviewWindowHeight} from '@Components/social-media-wizard/social-media-wizard-library';
import {SocialMediaWizardDesignSelectorPreview} from '@Components/social-media/components/social-media-wizard-design-selector-preview';
import {noop} from '@Utils/general.util';
import type {CreateDesignFromUploadOpts, OnPosterCreatedCallbackParams} from '@Components/design-selector/design-selector.types';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {openErrorModal} from '@Modals/error-modal';
import {requestResetAiCaptionInsights} from '@Libraries/auto-caption-library';
import {
  CACHE_KEY_FOR_SCHEDULING,
  convertDateToUTCTimestamp,
  getCaptionErrorAccountId,
  getTitleErrorAccountId,
  goBackToEvent,
  goBackToEventFromOneClickPromotion,
  isEventOneClickPromotionFlow,
  isEventsSocialPostingFlow,
} from '@/libraries/social-media';
import styles from './social-media-wizard.module.scss';
import {useAppDispatch} from '@/hooks';
import type {SocialMediaAccountId, SocialPost} from '../social-media/post.vo';
import {PostState} from '../social-media/post.vo';
import '@Global/modal';
import {openSocialMediaAddDesignModal} from '@Modals/social-media-add-design-modal/social-media-add-design-modal';
import {openDesignSelectorModal} from '@Modals/design-selector-modal/design-selector-modal';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 900,
  },
  large: {
    minWidth: 901,
    maxWidth: 1149,
  },
  xlarge: {
    minWidth: 1150,
  },
};

interface SocialMediaWizardProps {
  post?: SocialPost;
  accounts?: SocialAccount[];
  accountIdsForPost?: number[];
  designId?: string;
  templateId?: string;
  isGalleryFlow?: boolean;
  eventHashId?: string;
  numParentAccountsConnected?: number;
  hasSeenOnboarding?: boolean;
  hasSeenConfetti?: boolean;
  hasUserPublishedBefore?: boolean;
  isSocialMediaWizardAiCaptionsEnabled?: boolean;
  isGoogleBusinessProfileEnabled?: boolean;
  emphasizedPlatform?: SocialPlatform;
}

interface PopStateEventWithStep extends PopStateEvent {
  state: {
    step: SocialMediaWizardStep;
  };
}

// eslint-disable-next-line max-lines-per-function
export function SocialMediaWizard({
  post,
  accounts = [],
  accountIdsForPost = [],
  designId = undefined,
  templateId = undefined,
  isGalleryFlow = false,
  eventHashId = undefined,
  numParentAccountsConnected = 0,
  hasSeenOnboarding = false,
  hasSeenConfetti = false,
  hasUserPublishedBefore = false,
  isSocialMediaWizardAiCaptionsEnabled = false,
  isGoogleBusinessProfileEnabled = false,
  emphasizedPlatform = undefined,
}: SocialMediaWizardProps): ReactElement<any> {
  const accountTypeToConnect = useAccountTypeToConnect();
  const wizardStep = useWizardStep();
  const triggerNext = useTriggerNext();
  const nextButtonText = useNextButtonText();
  const wizardPost = useSocialMediaWizardPost();
  const isTemplateSelected = useIsTemplateSelected();
  const isSelectTemplateFlow = !isUserLoggedIn() || isTemplateSelected;
  const navView = useWizardMobileNavView();
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const publishingAction = useSocialPostPublishingAction();
  const prevStepRef = useRef<SocialMediaWizardStep | null>(wizardStep);
  const triggerPrevious = useTriggerPrevious();
  const dispatch = useAppDispatch();
  const [previousSteps, setPreviousSteps] = useState<SocialMediaWizardStep[]>([]);
  const [isLoadingEmbeddedEditor, setIsLoadingEmbeddedEditor] = useState(false);
  const [isDesignSelectorLoading, setDesignSelectorIsLoading] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [areAllPreviewsUpToDate, setAreAllPreviewsUpToDate] = useState(false);
  const [designIdsToPublish, setDesignIdsToPublish] = useState<PosterHashedId[]>([]);
  let isFluidContent = false;
  let mainContent = <SocialAccountButtons />;
  let header = '';
  const {containerQueryParamProps, containerRef, hasContainerQueryInitialized} = useSocialMediaWizardContainerQuery();
  const myRef: RefObject<HTMLElement | null> = containerRef as RefObject<HTMLElement | null>;
  const params = useContainerQuery(myRef, containerQuery);
  const previewParentRef = useRef<HTMLDivElement>(null);
  const designSelectorRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const connectedAccounts = useWizardConnectedAccounts();
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(containerRef as RefObject<HTMLElement | null>);
  const [bottomShadowSelector, setBottomShadowSelector] = useState(false);
  const designIdsToPublishTo = useDesignIdsToPublish();
  const postPublishingParams = useWizardPublishingParams();
  const designsGraphicsMeta = useWizardDesignsGraphicsMeta();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const socialPost = useSocialMediaWizardPost();
  const {setInAutoCaptionMode} = useAutoCaptionGlobal();
  const {fetchHasSeenStatus} = useHasSeenAiCaptionIntroPopup();
  const postId = getPostId();
  const designSelectorTab = useDesignSelectorTab();
  const isAiCaptionInsightsResetDone = useRef<boolean>(false);
  const MAX_UPLOADS_TO_DESIGNS = 10;
  const NOON_HOUR = 12;
  const ONE_HOUR_OFFSET = 1;
  const onUploadMediaDesignsCallbackRef = useRef((_: PosterHashedId[]) => {});
  const connectedAccountIds = connectedAccounts.map((account) => {
    return account.id;
  });

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    void fetchHasSeenStatus();
  }, []);

  useEffect(() => {
    if (!isUserPremium() || !connectedAccountIds.length || isAiCaptionInsightsResetDone.current || !postId) {
      return;
    }

    void requestResetAiCaptionInsights(postId, connectedAccountIds);
    isAiCaptionInsightsResetDone.current = true;
  }, [connectedAccountIds, postId]);

  const onOutsideAiCaptionPromptBoxClick = (): void => {
    setInAutoCaptionMode({accountId: null});
  };

  useEffect(() => {
    const removeListener = addClickOutsideListener(`.social-caption-container.-in-ai-mode`, onOutsideAiCaptionPromptBoxClick);
    return (): void => {
      removeListener();
    };
  }, []);

  const setWizardStep = (step: SocialMediaWizardStep | null): void => {
    if (step) {
      dispatch(updateWizardStep(step));
    }
  };

  const isSchedulingDateSameAsCurrentDate = (schedulingDate: Date): boolean => {
    const currentDate = new Date();
    return currentDate.getFullYear() === schedulingDate.getFullYear() && currentDate.getMonth() === schedulingDate.getMonth() && currentDate.getDate() === schedulingDate.getDate();
  };

  const setSchedulingDateForNoon = (schedulingDate: Date): void => {
    schedulingDate.setHours(NOON_HOUR, 0, 0, 0);
  };

  const setSchedulingDateForNextHour = (schedulingDate: Date): void => {
    const currentDate = new Date();
    const MIN_SCHEDULING_TIME_GAP_MINS = 15;
    const ONE_HR_MINS = 60;

    const currentMinutes = currentDate.getMinutes();
    const currentHours = currentDate.getHours();

    const minutesToNextHour = ONE_HR_MINS - currentMinutes;
    const isTimeToNextHrLessThanMinSchedulingGap = minutesToNextHour < MIN_SCHEDULING_TIME_GAP_MINS;

    if (isTimeToNextHrLessThanMinSchedulingGap) {
      schedulingDate.setHours(currentHours, currentMinutes + MIN_SCHEDULING_TIME_GAP_MINS, 0, 0);
    } else {
      schedulingDate.setHours(currentHours + ONE_HOUR_OFFSET, 0, 0, 0);
    }
  };

  const getDateObjectForPrefilledDate = (prefilledDate: string): Date => {
    const schedulingDate = new Date(prefilledDate);

    if (isSchedulingDateSameAsCurrentDate(schedulingDate)) {
      setSchedulingDateForNextHour(schedulingDate);
    } else {
      setSchedulingDateForNoon(schedulingDate);
    }

    return schedulingDate;
  };

  const setDetailsForSchedulingIfNeeded = (): void => {
    const prefilledDate = window.PMW.getCacheItem(CACHE_KEY_FOR_SCHEDULING) as string;

    if (!prefilledDate) {
      return;
    }

    const dateTime = getDateObjectForPrefilledDate(prefilledDate);
    const timeZoneOffset = dateTime.getTimezoneOffset();

    if (socialPost !== undefined) {
      const newPost: SocialPost = {
        ...socialPost,
        state: PostState.SCHEDULE_READY,
        publishOn: convertDateToUTCTimestamp(dateTime),
        timeZoneOffset,
      };

      dispatch(updatePost(newPost));
    }
  };

  useEffect(() => {
    if (designIdsToPublishTo.length === 0 && post) {
      const posterHashedIdsToSet = post.posterHashedIds;
      setDesignIdsToPublish(posterHashedIdsToSet);
      dispatch(updateDesignIdsToPublish(posterHashedIdsToSet));
      return;
    }

    if (isUserLoggedIn() && connectedAccountIds.length > 0) {
      void updatePublishingDesignMeta();
    } else if (templateId || designIdsToPublishTo.length > 0) {
      void fetchGraphicsMetaForAnonymousTemplates(designIdsToPublishTo[0] ?? templateId);
    }
  }, [designIdsToPublishTo, connectedAccountIds.length]);

  const resetResizeStatesOnClosingEmbeddedEditor = (): void => {
    dispatch(resetAccountResizeStatusForDesignsMap());
  };

  const updateDesignsMetaOnClosingEmbeddedEditor = async (): Promise<void> => {
    const wizardPostId = wizardPost ? wizardPost.id : null;
    const data = (await window.PMW.writeLocal('socialmedia/updateDesignsMetaForPublishing', {
      designIds: JSON.stringify(designIdsToPublishTo),
      postId: wizardPostId,
    })) as MultipleDesignMetaForPublishing;

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMapForDesigns) {
      dispatch(updateAccountNeedsResizeMapForDesigns(data.accountNeedsResizeMapForDesigns as AccountNeedsResizeMapForDesigns));
    }

    const designIsVideoMap: Record<PosterHashedId, boolean> = {};
    designIdsToPublishTo.forEach((posterId, index) => {
      designIsVideoMap[posterId] = data.designsIsVideo[index];
    });

    const multiplePreviewURLs = getPreviewURLs(designIsVideoMap);
    setDesignsMeta(multiplePreviewURLs, data.designsIsVideo, data.designsNames, designIdsToPublishTo);
  };

  const onEmbeddedEditorClosed = async (posterHashedId: string): Promise<void> => {
    const graphicsMeta: DesignsGraphicMetaPayload[] = [];
    const {hasAnyVideo} = await updatePublishingDesignMeta();

    designIdsToPublishTo.forEach((item) => {
      graphicsMeta.push({
        designId: item,
        previewURL: item === posterHashedId ? '' : designsGraphicsMeta[item].graphicURL,
        isVideo: item === posterHashedId ? false : designsGraphicsMeta[item].isVideo,
        name: designsGraphicsMeta[item].name,
      });
    });

    dispatch(updateDesignsGraphicsMeta(graphicsMeta));
    setIsPreviewLoading(true);

    pollForPosterPreviewChanges(posterHashedId, hasAnyVideo)
      .promise.then(async () => {
        resetResizeStatesOnClosingEmbeddedEditor();
        await updateDesignsMetaOnClosingEmbeddedEditor();
      })
      .catch((e: unknown) => {
        console.log(e);
      })
      .finally(() => {
        setIsPreviewLoading(false);
      });
  };

  const onEditDesignSelectionCallback = (posterHashedId: string): void => {
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(posterHashedId, onEmbeddedEditorOpened, () => {
      void onEmbeddedEditorClosed(posterHashedId);
    });
  };

  const isSingleDesignSelected = (): boolean => {
    return designIdsToPublishTo.length === 1;
  };

  const onClickEditDesign = (): void => {
    triggerEditDesignsFlow();
  };

  const triggerEditDesignsFlow = (): void => {
    if (isSingleDesignSelected()) {
      onEditDesignSelectionCallback(designIdsToPublishTo[0]);
      return;
    }

    const designActionModal: SocialPostsDesignActionModalProps = {
      designIdsToPublish: designIdsToPublishTo,
      designsGraphicsMeta,
      designActionCallback: onEditDesignSelectionCallback,
      actionPurpose: MultiPostDesignActionPurposes.EDIT,
      showRemoveDesignCta: true,
      removeDesignCallback: (designIds: string[]) => {
        setIsPreviewLoading(true);
        dispatch(updateDesignIdsToPublish(designIds));
      },
    };

    openDesignActionModal(designActionModal);
  };

  const addDesignModalMyUploadsSelectionCallback = (): void => {
    window.PMW.triggerCreateDesignFromUserUpload({
      maxSelectionNumber: MAX_UPLOADS_TO_DESIGNS,
      onPosterCreated: noop,
      onAllPostersCreated: (posterHashedIds: string[]) => {
        const updatedDesignIds = [...designIdsToPublishTo, ...getFilteredDesignIds(posterHashedIds)];
        dispatch(updateDesignIdsToPublish(updatedDesignIds));
        dispatch(enableNextButton());
      },
      onPosterCreationsStarted: (): void => {
        setIsPreviewLoading(true);
        dispatch(disableNextButton());
      },
      footerPrimaryCtaText: window.i18next.t('pmwjs_add_selected'),
      hideFooterSecondaryActions: true,
    });
  };

  const addDesignModallMyDesignsSelectionCallback = (): void => {
    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.MY_DESIGNS, DesignSelectorTab.SHARED_WITH_ME],
      onAddSelected: (selectedDesignIds: string[]) => {
        setIsPreviewLoading(true);
        setDesignIdsToPublish(selectedDesignIds);
        dispatch(updateDesignIdsToPublish(selectedDesignIds));
      },
      selectedDesignIds: designIdsToPublishTo,
    });
  };

  const onAddDesignGalleryTemplateSelected = (templateIdSelected: PosterHashedId): void => {
    setIsPreviewLoading(true);
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(
      templateIdSelected,
      onEmbeddedEditorOpened,
      (newDesignId: string) => {
        dispatch(updateDesignIdsToPublish([...designIdsToPublishTo, newDesignId]));
      },
      () => {
        setIsPreviewLoading(false);
      },
      EmbeddedEditorAction.COPY
    );
  };

  const addDesignModalCreateDesignSelectionCallback = (): void => {
    const designGalleryHeight = '88vh';

    openDesignSelectorModal({
      designSelectorTabsToShow: [DesignSelectorTab.GALLERY_TEMPLATES],
      selectedDesignIds: designIdsToPublishTo,
      designGalleryStartingURI: getStartingURIForGallery(),
      onDesignSelectionCallback: onAddDesignGalleryTemplateSelected,
      designGalleryHeight: designGalleryHeight,
    });
  };

  const onAddDesignClick = (): void => {
    openSocialMediaAddDesignModal({
      myUploadsSelectionCallback: addDesignModalMyUploadsSelectionCallback,
      myDesignsSelectionCallback: addDesignModallMyDesignsSelectionCallback,
      createDesignSelectionCallback: addDesignModalCreateDesignSelectionCallback,
    });
  };

  const getAddDesignCta = (): ReactElement<any> => {
    return (
      <Button
        type={Type.SECONDARY}
        icon="icon icon-plus"
        size={Size.SMALL}
        customClasses={`${styles.buttonShadow} ${styles.editDesignButton} spacing-m-r-2`}
        text={window.i18next.t('pmwjs_add_design')}
        iconPlacement={IconPlacement.LEFT}
        onClick={onAddDesignClick}
      />
    );
  };

  const getEditDesignCta = (): ReactElement<any> => {
    return (
      <Button
        type={Type.SECONDARY}
        icon="icon icon-pencil"
        size={Size.SMALL}
        customClasses={`${styles.buttonShadow} ${styles.editDesignButton}`}
        text={window.i18next.t('pmwjs_edit_design_capitalized')}
        iconPlacement={IconPlacement.LEFT}
        onClick={onClickEditDesign}
        isLoading={isLoadingEmbeddedEditor}
      />
    );
  };

  const getPreviewContentCta = (): ReactElement<any> | null => {
    if (isPreviewLoading || isSelectTemplateFlow) {
      return null;
    }

    return (
      <div className="flex-h-row">
        {getAddDesignCta()}
        {getEditDesignCta()}
      </div>
    );
  };

  const onLoadCallback = (): void => {
    setIsPreviewLoading(false);
  };

  const getPreviewStyles = (): NonNullable<unknown> | undefined => {
    if (!isMobileView()) {
      return undefined;
    }

    return {
      height: `${getMobilePreviewWindowHeight()}px`,
    };
  };

  const onOrphanedPreviewContainerClick = (): void => {
    if (isSelectTemplateFlow || isPreviewLoading) {
      return;
    }

    triggerEditDesignsFlow();
  };

  const getSocialPostContent = (): ReactElement<any> | null => {
    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS && !areAllPreviewsUpToDate) {
      return null;
    }

    return (
      <SocialPostContent
        designIdsToPublish={designIdsToPublishTo}
        onLoadCallback={onLoadCallback}
        areDetailsLoading={false}
        parentContainerRef={previewParentRef}
        type={SocialPostContentType.ORPHANED_PREVIEW}
        onOrphanedPreviewContainerClick={onOrphanedPreviewContainerClick}
        accountsToPublishTo={[]}
        publishingParams={postPublishingParams}
        designsGraphicsMeta={designsGraphicsMeta}
      />
    );
  };

  const getPreviewContentWrapperClasses = (): string => {
    return `${styles.previewWrapper} ${!getPreviewContentCta() ? styles.anonTemplateWrapper : ''} ${wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ? styles.selectAccountsStepWrapper : ''} `;
  };

  const getPreviewContent = (): ReactElement<any> | undefined => {
    const isLoading = wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ? isPreviewLoading && !areAllPreviewsUpToDate : isPreviewLoading;

    return (
      <div ref={previewParentRef} style={getPreviewStyles()} className={getPreviewContentWrapperClasses()}>
        <CircularProgressLoader classes={isLoading ? '_position-absolute' : '_hidden'} size={LOADER_SIZE.MEDIUM} />
        <div className={`_full-height _full-width ${isLoading ? '_invisible' : ''}`}>{getSocialPostContent()}</div>
        {getPreviewContentCta()}
      </div>
    );
  };

  const isMobileView = (): boolean => {
    return params.small || params.xsmall;
  };

  let previewContent: ReactElement<any> | undefined = <>{getPreviewContent()}</>;

  const getPreviewURLs = (designIsVideoMap: Record<PosterHashedId, boolean>): string[] => {
    const multiplePreviewURLs: string[] = [];
    const designIds = Object.keys(designIsVideoMap);

    designIds.forEach((item) => {
      multiplePreviewURLs.push(getPosterPreviewURL(item, designIsVideoMap[item]));
    });

    return multiplePreviewURLs;
  };

  const setDesignsMeta = (multiplePreviewUrls: string[], designsIsVideo: boolean[], designsNames: string[], freshDesignIds: PosterHashedId[] = []): void => {
    const graphicsMeta: DesignsGraphicMetaPayload[] = [];
    const designIdsToSet = freshDesignIds.length > 0 ? freshDesignIds : designIdsToPublishTo;
    for (let i = 0; i < designIdsToPublishTo.length; i++) {
      graphicsMeta.push({
        designId: designIdsToSet[i],
        previewURL: multiplePreviewUrls[i],
        isVideo: designsIsVideo[i],
        name: designsNames[i],
      });
    }

    dispatch(updateDesignsGraphicsMeta(graphicsMeta));
  };

  const onEmbeddedEditorOpened = (): void => {
    setIsLoadingEmbeddedEditor(false);
  };

  const linkSocialPostToEvent = (): void => {
    const idPost = wizardPost ? wizardPost.id : null;
    if (!eventHashId) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/linkSocialPostToEvent'), {
      idPost,
      eventHashId,
    });
  };
  const updatePublishingDesignMeta = async (freshDesignId?: PosterHashedId): Promise<DesignMetaUpdateInfo> => {
    if (!designIdsToPublishTo.length) {
      return {hasAnyVideo: false, areAllAccountsSupported: true};
    }

    const designIdsToUpdate = freshDesignId ? [freshDesignId] : designIdsToPublishTo;

    const wizardPostId = wizardPost ? wizardPost.id : null;
    const data = (await window.PMW.writeLocal('socialmedia/updateDesignsMetaForPublishing', {
      designIds: JSON.stringify(designIdsToUpdate),
      postId: wizardPostId,
    })) as MultipleDesignMetaForPublishing;

    const designMetaUpdateInfo = {
      hasAnyVideo: data.designsIsVideo[0],
      areAllAccountsSupported: true,
    };

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMapForDesigns) {
      const resizeMap = data.accountNeedsResizeMapForDesigns as AccountNeedsResizeMapForDesigns;
      designMetaUpdateInfo.areAllAccountsSupported = areAllAccountsSupportingDesignType(resizeMap);
      dispatch(updateAccountNeedsResizeMapForDesigns(resizeMap));
    }

    const designIsVideoMap: Record<PosterHashedId, boolean> = {};
    designIdsToUpdate.forEach((posterId, index) => {
      designIsVideoMap[posterId] = data.designsIsVideo[index];
    });

    const multiplePreviewURLs = getPreviewURLs(designIsVideoMap);
    setDesignsMeta(multiplePreviewURLs, data.designsIsVideo, data.designsNames, designIdsToUpdate);

    return designMetaUpdateInfo;
  };

  const isSizeNotSupportedForAccountId = (accountId: number, accountResizingNeededMap: AccountNeedsResizeMapForDesigns): boolean => {
    let resizingMap;

    for (const designHashedIdKey in accountResizingNeededMap[accountId]) {
      resizingMap = accountResizingNeededMap[accountId][designHashedIdKey];
      if (resizingMap.needsResizing === PosterResizeRequiredStatus.NOT_SUPPORTED || resizingMap.reelNeedsResizing === PosterResizeRequiredStatus.NOT_SUPPORTED) {
        return true;
      }
    }

    return false;
  };

  const areAllAccountsSupportingDesignType = (accountResizingNeededMap: AccountNeedsResizeMapForDesigns | null): boolean => {
    let areSupported = true;

    if (accountResizingNeededMap) {
      accountIdsToPublishTo.forEach((accountId) => {
        if (isSizeNotSupportedForAccountId(accountId, accountResizingNeededMap)) {
          areSupported = false;
        }
      });
    }

    return areSupported;
  };

  const shouldShowScheduleDetails = (): boolean => {
    if (!wizardPost?.state) {
      return false;
    }

    if (
      wizardPost.state === PostState.SCHEDULED ||
      wizardPost.state === PostState.PUBLISHING ||
      wizardPost.state === PostState.DRAFT ||
      wizardPost.state === PostState.ERROR ||
      wizardPost.state === PostState.SCHEDULE_ERROR
    ) {
      return false;
    }

    return !!(wizardPost?.publishOn && wizardPost.timeZoneOffset !== null && wizardStep === SocialMediaWizardStep.ADD_DETAILS);
  };

  const isAccountRelatedStep = (step: SocialMediaWizardStep): boolean => {
    return step === SocialMediaWizardStep.ACCOUNT_CONNECTION || step === SocialMediaWizardStep.SELECT_ACCOUNTS || step === SocialMediaWizardStep.CONNECT_ACCOUNTS;
  };

  const doUpdateNextButtonIfNeeded = (step: SocialMediaWizardStep): void => {
    if (isSelectTemplateFlow && isAccountRelatedStep(step)) {
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_next_edit_template')));
      dispatch(updateNextButtonIcon('icon-arrow-right'));
    } else if (step === SocialMediaWizardStep.ADD_DETAILS) {
      if (shouldShowScheduleDetails()) {
        dispatch(updateNextButtonText(window.i18next.t('pmwjs_schedule')));
        dispatch(updateNextButtonIcon('icon-calendar'));
        return;
      }

      dispatch(updateNextButtonText(window.i18next.t('pmwjs_publish')));
      dispatch(updateNextButtonIcon('icon-publish-arrow'));
    } else if (nextButtonText !== window.i18next.t('pmwjs_next')) {
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_next')));
      dispatch(updateNextButtonIcon('icon-arrow-right'));
    }
  };

  const onNext: VoidFunction = (): void => {
    initPanelSpecificHandlersOnNext();
    const nextStep = getNextWizardStep(post, accounts, wizardStep, {
      isGalleryFlow: isSelectTemplateFlow,
      emphasizedPlatform,
      templateId,
    });

    if (nextStep) {
      initPanelSpecificHandlersOnCurrentStep(nextStep);

      const isAnyAccountErrorProne = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) !== null || getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null;

      if (isAnyAccountErrorProne && nextStep === SocialMediaWizardStep.PUBLISH) {
        return;
      }

      setWizardStep(nextStep);
      setDetailsForSchedulingIfNeeded();
    }
  };

  const getKeyForPublishParams = (accountId: SocialMediaAccountId): string => {
    return `publish-params-${accountId}`;
  };

  const scrollToAccountSection = (accountId: SocialMediaAccountId): void => {
    const publishParamHandlerClass = PUBLISH_PARAM_HANDLER_CLASS;
    const yOffset = -100;
    const element = document.querySelectorAll(`.${publishParamHandlerClass}[data-key="${getKeyForPublishParams(accountId)}"]`)[0];
    const y = element.getBoundingClientRect().top + yOffset + window.scrollY;

    window.scrollTo({top: y, behavior: 'smooth'});
  };

  const initPanelSpecificHandlersOnCurrentStep = (currentStep: SocialMediaWizardStep): void => {
    const captionErrorAccount = getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors);
    const titleErrorAccount = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors);
    const isAnyAccountErrorProne = captionErrorAccount !== null || titleErrorAccount !== null;

    if (currentStep === SocialMediaWizardStep.PUBLISH && isAnyAccountErrorProne) {
      dispatch(updatePublishingAction(undefined));
      scrollToAccountSection(captionErrorAccount || titleErrorAccount || 123);
      return;
    }

    if (currentStep === SocialMediaWizardStep.ADD_DETAILS) {
      dispatch(initPublishingParams());
      dispatch(syncPublishingParams());
      void dispatch(
        savePreferredProfileSelectionUser({
          socialMediaAccountIds: accountIdsToPublishTo,
          idPost: wizardPost?.id,
        })
      );
    }

    if (currentStep === SocialMediaWizardStep.PUBLISH) {
      if (publishingAction === undefined && !isAnyAccountErrorProne) {
        dispatch(updatePublishingAction(SocialPostPublishingAction.PUBLISH));
      }
    } else {
      dispatch(updatePublishingAction(undefined));
    }
  };

  const doToggleNextButtonVisibility = (): void => {
    dispatch(showNextButton());
  };

  const initPanelSpecificHandlersOnNext = (): void => {
    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      setBottomShadowSelector(false);
      const designIds = designIdsToPublish;
      dispatch(showNextButton());

      if (designIds.length > 0) {
        dispatch(updateDesignIdsToPublish(designIds));
      }
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS) {
      dispatch(resetAccountResizeStatusForDesignsMap());
    }
  };

  const resetSocialPostCoverInPublishingParamsIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    const isPreviouslySelectedPostSingleGraphic = designIdsToPublishTo.length === 1;
    const shouldResetCoverForReselectedDesign = newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isPreviouslySelectedPostSingleGraphic;

    if (shouldResetCoverForReselectedDesign) {
      dispatch(resetSocialPostCoverForAllPublishingParams());
    }
  };

  const cleanupTemplateFlowIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    if (newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isTemplateSelected) {
      setDesignIdsToPublish([]);
      dispatch(updateDesignIdsToPublish([]));
      dispatch(updateIsTemplateSelected(false));
    }
  };

  const onBack: VoidFunction = (): void => {
    const previousStep: SocialMediaWizardStep | undefined = previousSteps.pop();

    if (previousStep) {
      prevStepRef.current = null;
      resetSocialPostCoverInPublishingParamsIfNeeded(previousStep);
      cleanupTemplateFlowIfNeeded(previousStep);
      setWizardStep(previousStep);
    } else {
      if (isEventsSocialPostingFlow()) {
        goBackToEvent();
        return;
      }

      if (isEventOneClickPromotionFlow()) {
        goBackToEventFromOneClickPromotion();
        return;
      }

      if (!document.referrer || window.PMW.isReferrer()) {
        // eslint-disable-next-line react-compiler/react-compiler
        window.location.href = window.PMW.util.site_url('posters/mine#/posts');
        return;
      }
      window.location.href = document.referrer;
    }
  };

  const getSanitizedDesignIds = (posterHashedIds: PosterHashedId[]): PosterHashedId[] => {
    const sanitizedIds: PosterHashedId[] = [];

    posterHashedIds.forEach((posterHashedId) => {
      if (posterHashedId) {
        sanitizedIds.push(posterHashedId);
      }
    });

    return sanitizedIds;
  };

  const onDesignListChange = (designsToPublish: PosterHashedId[]): void => {
    if (designsToPublish.length === 0) {
      dispatch(disableNextButton());
    } else {
      dispatch(enableNextButton());
    }

    const sanitizedIds = getSanitizedDesignIds(designsToPublish);
    setDesignIdsToPublish([...sanitizedIds]);
  };

  useEffect((): VoidFunction => {
    const previewContainerDiv = previewContainerRef;
    const designSelectorDiv = designSelectorRef;

    if (previewContainerDiv.current && designSelectorDiv.current) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === previewContainerDiv.current && entry.isIntersecting) {
            setBottomShadowSelector(false);
          } else {
            setBottomShadowSelector(true);
          }
        });
      });

      observer.observe(previewContainerDiv.current);
      observer.observe(designSelectorDiv.current);

      return () => {
        observer.disconnect();
      };
    }

    return noop;
  }, [designSelectorRef.current, previewContainerRef.current]);

  const getDesignSelectorPreviewClasses = (): string => {
    const mobileViewClasses = isMobileView() ? styles.carouselClasses : ``;

    if (bottomShadowSelector) {
      return `${mobileViewClasses} ${styles.bottomShadow}`;
    }

    return mobileViewClasses;
  };

  const shouldShowDesignSelectorPreview = (): boolean => {
    return designIdsToPublish.length !== 0 && designSelectorTab !== DesignSelectorTab.GALLERY_TEMPLATES;
  };

  const onGalleryTemplateSelected = (templateIdSelected: PosterHashedId): void => {
    dispatch(updateIsTemplateSelected(true));
    setDesignIdsToPublish([templateIdSelected]);
    dispatch(updateDesignIdsToPublishAndTriggerNext([templateIdSelected]));
  };

  const getDesignSelectorPreview = (): ReactElement<any> | null => {
    if (!shouldShowDesignSelectorPreview()) {
      return null;
    }

    return (
      <SocialMediaWizardDesignSelectorPreview
        containerClasses={getDesignSelectorPreviewClasses()}
        onUpdateDesignIdsToPublishTo={onDesignListChange}
        designIdsToPublishTo={designIdsToPublish}
      />
    );
  };

  const getSelectorContainerIfNeeded = (): ReactElement<any> | null => {
    if (designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      return null;
    }

    return <div ref={previewContainerRef} className={`${styles.selectorContainer} ${isMobileView() ? styles.mobileView : ''}`} />;
  };

  const onTabSelected = (newTab: DesignSelectorTab): void => {
    if (newTab === DesignSelectorTab.GALLERY_TEMPLATES) {
      setDesignIdsToPublish([]);
    }

    dispatch(updateDesignSelectorTab(newTab));
  };

  const removeExtraPosterIdsIfNeeded = (posterHashedIds: PosterHashedId[]): PosterHashedId[] => {
    const splicedPosterHashedIds: PosterHashedId[] = posterHashedIds;

    if (posterHashedIds.length > MAX_UPLOADS_TO_DESIGNS) {
      splicedPosterHashedIds.splice(MAX_UPLOADS_TO_DESIGNS);
    }

    return splicedPosterHashedIds;
  };

  const getFilteredDesignIds = (posterHashedIds: PosterHashedId[]): PosterHashedId[] => {
    const splicedIds = removeExtraPosterIdsIfNeeded(posterHashedIds);
    return getSanitizedDesignIds(splicedIds);
  };

  onUploadMediaDesignsCallbackRef.current = (posterHashedIds: PosterHashedId[]): void => {
    const mergedIds = [...designIdsToPublish, ...getFilteredDesignIds(posterHashedIds)];
    setDesignIdsToPublish(mergedIds);
    setDesignSelectorIsLoading(false);
    updateTriggerDelayedNext();
  };

  /**
   * Needed because useState has no callbacks availables similar to this.setState(..., [callback]);
   */
  const updateTriggerDelayedNext = (): void => {
    setTimeout((): void => {
      dispatch(updateTriggerNext());
    }, 100);
  };

  const getStartDesignFromUploadBtnOpts = (): CreateDesignFromUploadOpts => {
    return {
      maxSelectionNumber: MAX_UPLOADS_TO_DESIGNS,
      onAllPostersCreated: (posterHashedIds: PosterHashedId[]): void => {
        onUploadMediaDesignsCallbackRef.current(posterHashedIds);
      },
      onPosterCreated: (opts: OnPosterCreatedCallbackParams): void => {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CREATED, {[GA4EventParamName.TYPE]: opts.posterCreatedFromMediaType});
      },
      onTriggered: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_UPLOADMEDIA_CLICKED);
      },
      onPosterCreationsStarted: (): void => {
        setDesignSelectorIsLoading(true);
        dispatch(disableNextButton());
      },
    };
  };

  const getDesignGalleryHeightForSelectDesignStep = (): string => {
    const designGalleryBottomOffset = 250;
    return `${window.innerHeight - designGalleryBottomOffset}px`;
  };

  const getDesignSelectorComponent = (): ReactElement<any> => {
    return (
      <div>
        <DesignSelector
          containerRef={designSelectorRef}
          selectedDesignIds={designIdsToPublish}
          onDesignListChange={onDesignListChange}
          onDesignSelectionCallback={onGalleryTemplateSelected}
          designSelectorTab={designSelectorTab}
          onTabSelected={onTabSelected}
          startDesignFromUploadBtnOpts={getStartDesignFromUploadBtnOpts()}
          isLoading={isDesignSelectorLoading}
          designGalleryStartingURI={getStartingURIForGallery()}
          designGalleryHeight={getDesignGalleryHeightForSelectDesignStep()}
        />
        {getDesignSelectorPreview()}
        {getSelectorContainerIfNeeded()}
      </div>
    );
  };

  const getDesignSelectorTab = (): DesignSelectorTab => {
    if (isGalleryFlow) {
      return DesignSelectorTab.GALLERY_TEMPLATES;
    }

    return DesignSelectorTab.MY_DESIGNS;
  };

  const setSelectDesignPanel = (): void => {
    if (isSelectTemplateFlow && !isUserLoggedIn()) {
      mainContent = <DesignGallery onDesignSelected={onGalleryTemplateSelected} height="83.5vh" startingPageURI={getStartingURIForGallery()} />;
    } else {
      mainContent = getDesignSelectorComponent();
    }

    previewContent = undefined;
    header = window.i18next.t('pmwjs_start_by_selecting_design');
  };

  const waitForFreshPreview = (): void => {
    setIsPreviewLoading(true);
  };

  const onFreshPreviewFetched = (): void => {
    setIsPreviewLoading(false);
  };

  async function onEmbeddedEditorForTemplateClosed(designIdEdited: string): Promise<void> {
    if (designIdEdited !== designIdsToPublish[0]) {
      dispatch(syncEditedDesignId(designIdEdited));
    }

    const {areAllAccountsSupported, hasAnyVideo} = await updatePublishingDesignMeta(designIdEdited);
    waitForFreshPreview();

    pollForPosterPreviewChanges(designIdEdited, hasAnyVideo)
      .promise.then(async (): Promise<void> => {
        onFreshPreviewFetched();
        await updatePublishingDesignMeta(designIdEdited);
      })
      .catch((e: unknown) => {
        console.log(e);
      });

    if (areAllAccountsSupported) {
      dispatch(updateTriggerNext());
      dispatch(hideAccountLevelErrorMessage());
    } else {
      dispatch(
        updateAccountLevelErrorMessage({
          title: window.i18next.t('pmwjs_unsupported_account_error_title'),
          description: window.i18next.t('pmwjs_unsupported_account_error_desc'),
        })
      );
      dispatch(updateTriggerPrevious());
    }
  }

  const setEditTemplatePanel = (): void => {
    mainContent = (
      <TemplateEditorPanel
        onSave={(designIdSaved) => {
          void onEmbeddedEditorForTemplateClosed(designIdSaved);
        }}
        onClose={onCancel}
      />
    );
    header = '';
    previewContent = undefined;
  };

  const onCancel = (): void => {
    dispatch(updateTriggerPrevious());
  };

  const getMainContentForConnectAccountsPanel = (): ReactElement<any> => {
    const numAccountsConnected = connectedAccounts.length;

    if (numAccountsConnected === 0) {
      return <SocialAccountButtons />;
    }

    return (
      <>
        <div className="spacing-m-b-4 flex-row-align-center">
          <Icon icon="icon-back" size={IconSize.SIZE_ICON_20} type={IconType.GHOST} shape={IconShape.SQUARE} className="spacing-m-r-2" onClick={onCancel} />
          <Text size={TextSize.SMALL} className="spacing-m-l-2" val={window.i18next.t('pmwjs_add_another_account')} bold />
        </div>
        <SocialAccountButtons />
      </>
    );
  };

  const setConnectAccountsPanel = (): void => {
    mainContent = getMainContentForConnectAccountsPanel();
    header = window.i18next.t('pmwjs_where_to_publish');
  };

  const setAccountConnectionPanel = (): void => {
    if (accountTypeToConnect) {
      mainContent = <SocialAccountConnection accountTypeToConnect={accountTypeToConnect} />;
      header = window.i18next.t('pmwjs_where_to_publish');
    }
  };

  const setSelectAccountsPanel = (): void => {
    mainContent = <SocialAccountsList />;
    header = window.i18next.t('pmwjs_one_or_more_accounts');
  };

  const onClickAddAccount = (): void => {
    setWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS);
  };

  const setPublishingPanel = (): void => {
    if (accountIdsToPublishTo.length > 0) {
      header = publishingAction ? getLoadingTextByPublishingAction(publishingAction) : window.i18next.t('pmwjs_publishing');
      mainContent = <SocialMediaPostDetails onClickAddAccount={onClickAddAccount} size={getWizardContentSize(params)} key={'publishing-panel'} />;
      previewContent = undefined;
    }
  };

  const setAddDetailsPanel = (): void => {
    if (accountIdsToPublishTo.length > 1) {
      header = window.i18next.t('pmwjs_add_details_to_your_posts_x', {x: accountIdsToPublishTo.length});
    } else {
      header = window.i18next.t('pmwjs_add_details_to_your_post');
    }
    mainContent = <SocialMediaPostDetails onClickAddAccount={onClickAddAccount} size={getWizardContentSize(params)} key={'details-panel'} />;
    previewContent = undefined;
  };

  const isInvalidStepCombination = (): boolean => {
    return (prevStepRef.current === SocialMediaWizardStep.SELECT_DESIGN || wizardStep === SocialMediaWizardStep.SELECT_DESIGN) && post !== null;
  };

  const populateHistoryStack = (): void => {
    if (wizardStep && prevStepRef.current && !isInvalidStepCombination()) {
      const updatedPreviousStep = previousSteps;
      updatedPreviousStep.push(prevStepRef.current);
      setPreviousSteps(updatedPreviousStep);
    }

    prevStepRef.current = wizardStep;
  };

  const onPopStateChange = (popStateEvent: PopStateEventWithStep): void => {
    if (typeof popStateEvent.state === 'object') {
      const {step} = popStateEvent.state;

      if (step) {
        dispatch(updateWizardStep(step));
        setIsNavigatingBack(true);
      }
    }
  };

  const updateWindowHistoryState = (step: SocialMediaWizardStep): void => {
    if (!isNavigatingBack) {
      window.history.pushState({step}, '', '');
    }
  };

  useUpdatePublishingParams();

  useDidUpdateEffect(() => {
    populateHistoryStack();
    doUpdateNextButtonIfNeeded(wizardStep);
  }, [wizardStep]);

  useEffect(() => {
    doToggleNextButtonVisibility();

    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      dispatch(updateDesignSelectorTab(getDesignSelectorTab()));

      if (designIdsToPublish.length === 0) {
        dispatch(disableNextButton());
      }

      setDesignIdsToPublish(designIdsToPublishTo);
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS) {
      setIsPreviewLoading(true);
      void checkUpdateInGeneratedPreviewsStatus(designIdsToPublishTo);
    }

    // codeReviewAhsan: review this check if needed
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      if (isEventsSocialPostingFlow()) {
        linkSocialPostToEvent();
      }
    }

    updateWindowHistoryState(wizardStep);
  }, [wizardStep]);

  const checkUpdateInGeneratedPreviewsStatus = async (posterHashedIds: string[]): Promise<void> => {
    if (!isUserLoggedIn()) {
      return;
    }

    const pollingHaveAllUpdatedPreviewsBeenGenerated = async (): Promise<void> => {
      return (await window.PMW.readLocal('poster/haveAllUpdatedPreviewsBeenGenerated', {posterHashedIds})) as Promise<void>;
    };

    try {
      const POLLING_REQUEST_TIMEOUT = 600000;

      const allGenerated = (await window.PMW.pollingAjaxCallAsync(
        async (): Promise<void> => {},
        pollingHaveAllUpdatedPreviewsBeenGenerated,
        POLLING_REQUEST_TIMEOUT
      )) as AjaxSuccessResponse;

      if (allGenerated) {
        setAreAllPreviewsUpToDate(true);
        return;
      }

      openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_poster_previews')});
    } catch (e) {
      openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_posters')});
    }
    return;
  };

  const fetchGraphicsMetaForAnonymousTemplates = async (designIdToFetch: string): Promise<void> => {
    const data = (await window.PMW.writeLocal('socialmedia/getIsDesignAVideo', {
      designId: designIdToFetch,
    })) as DesignIsVideoInformation;

    const isFetchDesignAVideo = data.designIsVideo;
    const fetchedDesignName = data.designName;

    const designIsVideoMap: Record<PosterHashedId, boolean> = {};
    designIsVideoMap[designIdToFetch] = isFetchDesignAVideo;

    const multiplePreviewURLs = getPreviewURLs(designIsVideoMap);
    setDesignsMeta(multiplePreviewURLs, [isFetchDesignAVideo], [fetchedDesignName]);
  };

  const toggleNextButtonByAccountsConnected = (): void => {
    if (connectedAccounts.length === 0) {
      dispatch(disableNextButton());
    } else if (accountIdsToPublishTo.length !== 0) {
      dispatch(enableNextButton());
    }
  };

  const getSanitizedEmphasizedPlatform = (): SocialPlatform | undefined => {
    return emphasizedPlatform ? emphasizedPlatform : undefined;
  };

  useEffect(() => {
    toggleNextButtonByAccountsConnected();
  }, [connectedAccounts]);

  const handleExistingDesignFromURL = (designIdToUpdate: string): void => {
    dispatch(updateDesignIdsToPublish([designIdToUpdate]));
    setDesignIdsToPublish([designIdToUpdate]);
  };

  useEffect(() => {
    setIsNavigatingBack(false);
    window.addEventListener('popstate', onPopStateChange);
    setWizardStep(
      getNextWizardStep(post, accounts, null, {
        isGalleryFlow: isSelectTemplateFlow,
        templateId,
        emphasizedPlatform,
      })
    );
    dispatch(updateHasSeenOnboarding(hasSeenOnboarding));
    dispatch(updateHasSeenConfetti(hasSeenConfetti));
    dispatch(updateHasUserPublishedBefore(hasUserPublishedBefore));
    dispatch(updateIsSocialMediaWizardAiCaptionsEnabled(isSocialMediaWizardAiCaptionsEnabled));
    dispatch(updateIsGoogleBusinessProfileEnabled(isGoogleBusinessProfileEnabled));
    dispatch(updateNumParentAccountsConnected(numParentAccountsConnected));

    const sanitizedPlatformToConnect = getSanitizedEmphasizedPlatform();

    if (sanitizedPlatformToConnect) {
      dispatch(updateEmphasizedPlatform(sanitizedPlatformToConnect));
      dispatch(updateAccountTypeToConnect(sanitizedPlatformToConnect));
    }

    initPanelSpecificHandlersOnCurrentStep(wizardStep);
    toggleNextButtonByAccountsConnected();

    if (accountIdsForPost.length > 0) {
      dispatch(updateAccountIdsToPublishTo(accountIdsForPost));
    }

    if (designId) {
      dispatch(updateDesignIdsToPublish([designId]));
    }

    if (isGalleryFlow) {
      dispatch(updateIsTemplateSelected(true));
    }

    if (templateId) {
      dispatch(updateIsTemplateSelected(true));
      handleExistingDesignFromURL(templateId);
    }

    if (post) {
      const {publishingParams} = post;
      if (publishingParams) {
        dispatch(updatePublishingParams(publishingParams));
      }

      dispatch(updatePost(post));
    }

    return (): void => {
      window.removeEventListener('popstate', onPopStateChange);
    };
  }, []);

  useDidUpdateEffect(() => {
    onNext();
  }, [triggerNext]);

  useDidUpdateEffect(() => {
    onBack();
  }, [triggerPrevious]);

  const setFluidContentIfNeeded = (): void => {
    isFluidContent = wizardStep === SocialMediaWizardStep.SELECT_DESIGN;
  };

  if (wizardStep) {
    setFluidContentIfNeeded();
    // eslint-disable-next-line default-case
    switch (wizardStep) {
      case SocialMediaWizardStep.SELECT_DESIGN:
        setSelectDesignPanel();
        break;
      case SocialMediaWizardStep.CONNECT_ACCOUNTS:
        setConnectAccountsPanel();
        break;
      case SocialMediaWizardStep.ACCOUNT_CONNECTION:
        if (!accountTypeToConnect) {
          openErrorModal({message: window.i18next.t('pmwjs_error_message')});
        }

        setAccountConnectionPanel();
        break;
      case SocialMediaWizardStep.SELECT_ACCOUNTS:
        setSelectAccountsPanel();

        break;
      case SocialMediaWizardStep.EDIT_TEMPLATE:
        setEditTemplatePanel();

        break;
      case SocialMediaWizardStep.ADD_DETAILS:
        setAddDetailsPanel();

        break;
      case SocialMediaWizardStep.PUBLISH:
        setPublishingPanel();
        break;
    }
  }

  const getNavSteps = (): string[] => {
    const steps: string[] = [];

    if (!post && !templateId) {
      steps.push(window.i18next.t('pmwjs_design'));
    }

    steps.push(window.i18next.t('pmwjs_accounts'));

    if (isSelectTemplateFlow) {
      steps.push(window.i18next.t('pmwjs_edit'));
    }

    steps.push(window.i18next.t('pmwjs_publish'));

    return steps;
  };

  const getFooter = (): ReactElement<any> | null => {
    const size = getWizardContentSize(params);
    const numberSteps = getNavSteps().length;

    if (wizardStep === SocialMediaWizardStep.PUBLISH) {
      return null;
    }

    if (size === SocialMediaWizardContentSize.XSMALL || size === SocialMediaWizardContentSize.SMALL) {
      return (
        <SocialMediaMobileFooter
          shouldShowShadow={bottomShadow && !isLoadingPreview()}
          onNext={onNext}
          onPrevious={onBack}
          nSteps={numberSteps}
          currentStep={previousSteps.length + 1}
          currentSelectedDesigns={designIdsToPublish}
        />
      );
    }

    return null;
  };

  const getContainerShadowClasses = (): string => {
    if (topShadow) {
      return styles.topShadow;
    }

    return '';
  };

  const isLoadingPreview = (): boolean => {
    return wizardStep !== SocialMediaWizardStep.PUBLISH && wizardStep !== SocialMediaWizardStep.ADD_DETAILS && navView === WizardMobileNavView.PREVIEW;
  };

  const getContainerClasses = (): string => {
    const size = getWizardContentSize(params);
    const isMobile = size === SocialMediaWizardContentSize.XSMALL || size === SocialMediaWizardContentSize.SMALL;
    const isLoading = isLoadingPreview();
    const numberAccountsToPublishTo = accountIdsToPublishTo.length;

    if (isMobile) {
      return `${!isLoading && numberAccountsToPublishTo > 1 ? styles.overflowClasses : '_full-height'} ${getContainerShadowClasses()} _full-width spacing-m-b-10`;
    }

    return `_full-width`;
  };

  const socialMediaWizardContainerQueriesContextValues = useMemo(() => {
    return {
      isExtraSmallScreen: containerQueryParamProps.isExtraSmallScreen,
      isSmallScreen: containerQueryParamProps.isSmallScreen,
      isMediumScreen: containerQueryParamProps.isMediumScreen,
      isLargeScreen: containerQueryParamProps.isLargeScreen,
      isExtraLargeScreen: containerQueryParamProps.isExtraLargeScreen,
    };
  }, [containerQueryParamProps]);

  return (
    <>
      <SocialMediaWizardNav currentSelectedDesigns={designIdsToPublish} navSteps={getNavSteps()} onNext={onNext} onBack={onBack} />
      <SocialMediaWizardContainerQueriesContext.Provider value={socialMediaWizardContainerQueriesContextValues}>
        <div ref={containerRef} className={getContainerClasses()}>
          {hasContainerQueryInitialized ? (
            <>
              <SocialMediaWizardContent
                size={getWizardContentSize(params)}
                heading={header}
                mainContent={mainContent}
                previewContent={previewContent}
                fluidWidth={isFluidContent}
              />
              <SocialMediaWizardOnboarding />
            </>
          ) : (
            <CircularProgressLoader classes="-for-mystuff-content" />
          )}
        </div>
      </SocialMediaWizardContainerQueriesContext.Provider>
      {getFooter()}
    </>
  );
}
